import React, { createContext, useContext } from 'react';
import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';
import i18n from '../i18n';
import { useAuth } from '../context/AuthContext';

const TourContext = createContext();

export const useTour = () => useContext(TourContext);

export function TourProvider({ children }) {
    const { user } = useAuth();

    const startTour = () => {
        // Use window.location.pathname — no need for useLocation hook,
        // which would require TourProvider to be inside a <Router>.
        const path = window.location.pathname;
        const lang = (i18n.language || 'en').startsWith('fr') ? 'fr' : 'en';

        const tour = new Shepherd.Tour({
            defaultStepOptions: {
                cancelIcon: { enabled: true },
                scrollTo: { behavior: 'smooth', block: 'center' },
                classes: 'custom-shepherd-theme',
            },
            useModalOverlay: true,
        });

        // ── Shared: Sidebar Navigation ──
        tour.addStep({
            id: 'nav',
            attachTo: { element: 'aside', on: 'right' },
            title: lang === 'fr' ? 'Navigation Principale' : 'Main Navigation',
            text: lang === 'fr'
                ? 'Utilisez cette barre pour naviguer entre vos actifs, certificats et rapports.'
                : 'Use this sidebar to navigate between your assets, certificates, and reports.',
            buttons: [{ classes: 'shepherd-button-primary', text: lang === 'fr' ? 'Suivant' : 'Next', action: tour.next }]
        });

        // ── Dashboard ──
        if (path === '/dashboard') {
            tour.addStep({
                id: 'dashboard-stats',
                attachTo: { element: 'main', on: 'top' },
                title: lang === 'fr' ? 'Tableau de Bord' : 'Dashboard',
                text: lang === 'fr'
                    ? 'Vue d\'ensemble en temps réel de votre portefeuille de GOs et de votre score de conformité.'
                    : 'Real-time overview of your GO portfolio and compliance score.',
                buttons: [{ classes: 'shepherd-button-primary', text: lang === 'fr' ? 'Terminer' : 'Done', action: tour.complete }]
            });
        }

        // ── Assets / Devices ──
        else if (path === '/devices') {
            tour.addStep({
                id: 'devices-register',
                attachTo: { element: 'a[href="/devices/register"]', on: 'bottom' },
                title: lang === 'fr' ? 'Enregistrer un Actif' : 'Register an Asset',
                text: lang === 'fr'
                    ? 'Cliquez ici pour déclarer une nouvelle centrale (Solaire, Éolien, H2). Renseignez le GSRN, la capacité en MW et la loi applicable (Loi 13-09 ou Loi 82-21).'
                    : 'Click here to register a new plant (Solar, Wind, H2). Provide the GSRN, capacity in MW, and applicable law (Law 13-09 or Law 82-21).',
                buttons: [{ classes: 'shepherd-button-primary', text: lang === 'fr' ? 'Terminer' : 'Done', action: tour.complete }]
            });
        }

        // ── Metering Portal ──
        else if (path === '/grid/metering') {
            tour.addStep({
                id: 'metering-select',
                attachTo: { element: 'table', on: 'bottom' },
                title: lang === 'fr' ? 'Sélection de la Centrale' : 'Select the Plant',
                text: lang === 'fr'
                    ? 'Cliquez sur une ligne pour sélectionner la centrale dont vous souhaitez saisir la lecture.'
                    : 'Click on a row to select the plant you want to submit a reading for.',
                buttons: [{ classes: 'shepherd-button-primary', text: lang === 'fr' ? 'Suivant' : 'Next', action: tour.next }]
            });
            tour.addStep({
                id: 'metering-form',
                attachTo: { element: 'form', on: 'left' },
                title: lang === 'fr' ? 'Saisie de la Lecture' : 'Enter Meter Reading',
                text: lang === 'fr'
                    ? 'Entrez le volume total (MWh). Pour une centrale Loi 82-21, deux champs supplémentaires apparaissent : "Self-Consumption" et "Grid Injected". Leur somme doit être égale au total.'
                    : 'Enter total volume (MWh). For a Law 82-21 plant, two extra fields appear: "Self-Consumption" and "Grid Injected". Their sum must equal the total.',
                buttons: [{ classes: 'shepherd-button-primary', text: lang === 'fr' ? 'Terminer' : 'Done', action: tour.complete }]
            });
        }

        // ── Certificates ──
        else if (path === '/certificates') {
            tour.addStep({
                id: 'cert-table',
                attachTo: { element: 'table', on: 'top' },
                title: lang === 'fr' ? 'Votre Portefeuille de GOs' : 'Your GO Portfolio',
                text: lang === 'fr'
                    ? 'Chaque ligne est un lot de certificats EECS. Téléchargez le PDF, transférez ou annulez via les boutons à droite.'
                    : 'Each row is an EECS certificate batch. Download the PDF, transfer, or cancel using the buttons on the right.',
                buttons: [{ classes: 'shepherd-button-primary', text: lang === 'fr' ? 'Terminer' : 'Done', action: tour.complete }]
            });
        }

        // ── Admin Approvals ──
        else if (path === '/admin/approvals') {
            tour.addStep({
                id: 'approvals-queue',
                attachTo: { element: 'main', on: 'top' },
                title: lang === 'fr' ? 'File d\'Approbation Souveraine' : 'Sovereign Approval Queue',
                text: lang === 'fr'
                    ? 'Les demandes de certification vérifiées par ONEE/VVB apparaissent ici. Cliquez sur "Mint GO" pour créer le certificat cryptographique national.'
                    : 'Certification requests verified by ONEE/VVB appear here. Click "Mint GO" to create the national cryptographic certificate.',
                buttons: [{ classes: 'shepherd-button-primary', text: lang === 'fr' ? 'Terminer' : 'Done', action: tour.complete }]
            });
        }

        // ── Transfers ──
        else if (path.startsWith('/transfers')) {
            tour.addStep({
                id: 'transfer-info',
                attachTo: { element: 'main', on: 'top' },
                title: lang === 'fr' ? 'Registre des Transferts' : 'Transfer Registry',
                text: lang === 'fr'
                    ? 'Consultez l\'historique de toutes les transactions GO. Les transferts sont verrouillés après 12 mois (RED II).'
                    : 'View the history of all GO transactions. Transfers are locked after 12 months (RED II).',
                buttons: [{ classes: 'shepherd-button-primary', text: lang === 'fr' ? 'Terminer' : 'Done', action: tour.complete }]
            });
        }

        // ── Fallback for other pages ──
        else {
            tour.addStep({
                id: 'generic-finish',
                attachTo: { element: 'a[href="/certificates"]', on: 'right' },
                title: lang === 'fr' ? 'Vos Certificats' : 'Your Certificates',
                text: lang === 'fr'
                    ? 'Retrouvez votre inventaire complet de Garanties d\'Origine ici.'
                    : 'Find your complete Guarantee of Origin inventory here.',
                buttons: [{ classes: 'shepherd-button-primary', text: lang === 'fr' ? 'Terminer' : 'Done', action: tour.complete }]
            });
        }

        tour.start();
    };

    return (
        <TourContext.Provider value={{ startTour }}>
            {children}
        </TourContext.Provider>
    );
}
